import React, { useState, useMemo, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ChevronDownIcon, 
  ArrowPathIcon,
  MagnifyingGlassIcon,
  PencilSquareIcon,
  XMarkIcon,
  FunnelIcon,
  CheckIcon,
  AdjustmentsHorizontalIcon
} from '@heroicons/react/20/solid';
import { AlertProps } from '../../Shared/Utils/Alert';
import { CandidateProfile } from '../../Candidate/Types/CandidateType';
import { candidateSearch } from '../Services/SearchFilterService';
import CandidateCardForRecruiter from '../../Recruiter/Components/CandidateCardForRecruiter';
import { searchCandidates } from '../../Candidate/Services/CandidateService';
import LoadMoreButton from '../../Shared/Pagination/LoadMoreButton';

// --- Types ---
type FilterType = "TEXT" | "NUMBER" | "BOOLEAN" | "MULTI" | "RANGE" | "GT" | "GTE" | "LT" | "LTE";

export interface FilterItem {
  id: string;
  label: string;
  fieldName: string;
  filterType: FilterType;
  tier: string;
  enabled: boolean;
  displayOrder: number;
}

interface SmartFilterSystemProps {
  filters: FilterItem[];
}

const MOCK_OPTIONS = ["Engineering", "Design", "Marketing", "Sales", "Product", "Legal"];

export default function SmartFilterSystem({ filters }: SmartFilterSystemProps) {
  const apiResponse = useMemo(() => {
    return [...filters].sort((a, b) => a.displayOrder - b.displayOrder);
  }, [filters]);

  const [filterValues, setFilterValues] = useState<Record<string, any>>({});
  const [isApplied, setIsApplied] = useState(false);
  const [candidates, setCandidates] = useState<CandidateProfile[]>([]);
  const [alert, setAlert] = useState<AlertProps | null>(null);

  /** * ADDITIONAL FEATURE: Accordion Logic
   * Tracks which filter ID is currently open. 
   * Initialized to null so all filters are closed on page load.
   */
  const [expandedSectionId, setExpandedSectionId] = useState<string | null>(null);

  const activeFilters = useMemo(() => {
    return apiResponse.filter(f => {
      const val = filterValues[f.id];
      if (val === undefined || val === null || val === '') return false;
      if (f.filterType === "RANGE") return val.min || val.max;
      if (f.filterType === "MULTI") return val.length > 0;
      return true;
    });
  }, [filterValues, apiResponse]);

  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);

  const [pagination, setPagination] = useState({
    page: 0,
    size: 2,
    totalElements: 0,
    totalPages: 0,
    first: true,
    last: false,
    hasNext: false,
    hasPrevious: false,
  });

  const handleValueChange = (id: string, value: any) => setFilterValues(prev => ({ ...prev, [id]: value }));
  
  const removeFilter = (id: string) => {
    const newValues = { ...filterValues };
    delete newValues[id];
    setFilterValues(newValues);
  };

  const resetFilters = () => { 
    setFilterValues({}); 
    setIsApplied(false); 
    setExpandedSectionId(null); // Close all on reset
  };

  const handleCreateSubmit = async () => {
    try {
      setLoading(true);
  
      const transformedFilters: Record<string, any> = {};
  
      apiResponse.forEach((filter) => {
        const value = filterValues[filter.id];
  
        if (
          value !== undefined &&
          value !== null &&
          value !== ""
        ) {
          transformedFilters[filter.fieldName] = value;
        }
      });
  
      const payload = {
        filters: transformedFilters,
      };
  
      const response = await searchCandidates(payload, page);
  
      if (response.success) {
        setCandidates((prev) =>
          page === 0
            ? response.data.content
            : [...prev, ...response.data.content]
        );
  
        setPagination({
          page: response.data.page,
          size: response.data.size,
          totalElements: response.data.totalElements,
          totalPages: response.data.totalPages,
          first: response.data.first,
          last: response.data.last,
          hasNext: response.data.hasNext,
          hasPrevious: response.data.hasPrevious,
        });
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false); // ← IMPORTANT
    }
  };




   useEffect(() => {
          if (page > 0) {
            handleCreateSubmit();
          }
        }, [page]);

  return (
    <div className="min-h-screen bg-[#F8FAFC] font-sans text-slate-700 antialiased">
      <nav className="bg-white/80 backdrop-blur-md border-b border-slate-200/60 px-4 md:px-8 py-3 md:py-4 sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-4">
            <div className="bg-gradient-to-br from-blue-600 to-indigo-700 p-2 rounded-xl shadow-lg shadow-blue-200">
              <FunnelIcon className="w-5 h-5 text-white" />
            </div>
            <h1 className="text-xl font-black text-slate-900 tracking-tight">Recruiter Workspace</h1>
          </div>
          <div className="flex items-center gap-6">
             {activeFilters.length > 0 && (
                <button onClick={resetFilters} className="group text-sm font-bold text-slate-500 hover:text-blue-600 flex items-center gap-2 transition-all">
                  <ArrowPathIcon className="w-4 h-4 group-hover:rotate-180 transition-transform duration-500" /> 
                  Reset Workspace
                </button>
             )}
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto flex flex-col md:flex-row gap-6 md:gap-8 p-4 md:p-8">
        <aside className="w-full md:w-80 shrink-0">
          <div className="bg-white rounded-3xl border border-slate-200 shadow-[0_8px_30px_rgb(0,0,0,0.04)] overflow-hidden sticky top-28">
            <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-white">
              <div className="flex items-center gap-2">
                <AdjustmentsHorizontalIcon className="w-5 h-5 text-slate-400" />
                <h2 className="font-bold text-slate-900 text-xs tracking-widest uppercase">Filters</h2>
              </div>
              <span className="bg-blue-600 text-white text-[11px] px-2.5 py-1 rounded-lg font-bold">
                {activeFilters.length}
              </span>
            </div>

            <div className="divide-y divide-slate-100 max-h-[65vh] overflow-y-auto custom-scrollbar">
              {apiResponse.map((filter) => (
                <FilterSection 
                  key={filter.id} 
                  config={filter} 
                  value={filterValues[filter.id]} 
                  onChange={(val) => handleValueChange(filter.id, val)}
                  // Pass the state control to child
                  isOpen={expandedSectionId === filter.id}
                  onToggle={() => setExpandedSectionId(expandedSectionId === filter.id ? null : filter.id)}
                />
              ))}
            </div>
            
            <div className="p-6 bg-slate-50/80 border-t border-slate-100">
                <button 
                  onClick={() => { handleCreateSubmit(); setIsApplied(true)}}
                  className="w-full py-4 bg-slate-900 text-white rounded-2xl font-bold hover:bg-blue-600 transition-all shadow-xl shadow-slate-200 active:scale-[0.98]"
                >
                  Apply Search
                </button>
            </div>
          </div>
        </aside>

        <main className="flex-1">
          <div className="bg-white rounded-3xl border border-slate-200 shadow-[0_8px_30px_rgb(0,0,0,0.04)] min-h-[700px] p-8">
            <div className="flex items-center justify-between mb-8">
               <div>
                  <h3 className="text-2xl font-black text-slate-900 tracking-tight">Candidate Search</h3>
                  <p className="text-sm text-slate-400 font-medium">Refine your search using the sidebar filters</p>
               </div>
               {isApplied && (
                 <button onClick={() => setIsApplied(false)} className="text-xs text-blue-600 font-black flex items-center gap-2 bg-blue-50/50 hover:bg-blue-100 px-4 py-2.5 rounded-xl transition-all border border-blue-100">
                    <PencilSquareIcon className="w-4 h-4" /> EDIT CRITERIA
                 </button>
               )}
            </div>

            <div className="flex flex-wrap gap-3 mb-10 min-h-[50px] p-4 bg-slate-50/50 rounded-2xl border-2 border-dashed border-slate-200">
              {activeFilters.length > 0 ? (
                activeFilters.map(f => (
                  <motion.span 
                    initial={{ scale: 0.9, opacity: 0 }} 
                    animate={{ scale: 1, opacity: 1 }}
                    key={f.id} 
                    className="flex items-center gap-3 bg-white text-slate-700 px-4 py-2 rounded-xl text-[12px] font-bold border border-slate-200 shadow-sm"
                  >
                    <span className="text-slate-400 uppercase text-[9px] tracking-wider">{f.label}</span> 
                    <span className="text-blue-600">{formatDisplayValue(f, filterValues[f.id])}</span>
                    <XMarkIcon onClick={() => removeFilter(f.id)} className="w-4 h-4 cursor-pointer hover:text-red-500 transition-colors" />
                  </motion.span>
                ))
              ) : (
                <div className="text-slate-400 text-sm flex items-center gap-3 italic">
                  <FunnelIcon className="w-4 h-4 opacity-30" />
                  No filters active. Use the left panel to start.
                </div>
              )}
            </div>

            <div className="grid gap-6">
              <div className="relative z-0 px-2 md:px-8">
              {loading && candidates.length === 0 ? (
                <div className="text-center py-14">
                  <div className="inline-block w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto" />
                  <p className="text-sm font-semibold text-slate-500 mt-4">Running search pipeline...</p>
                </div>
              ) : candidates.length === 0 ? (
                <div className="text-center py-14">
                  <p className="text-base font-black text-slate-400">No Candidate Found</p>
                  <p className="text-xs text-slate-400 mt-1">
                    Adjust the filters in the sidebar and run the search again.
                  </p>
                </div>
              ) : (
                <>
                  <p className="text-sm text-slate-500 font-medium mb-4">
                    {candidates.length.toLocaleString()} shown of{" "}
                    <span className="font-bold text-blue-600">
                      {pagination.totalElements.toLocaleString()}
                    </span>{" "}
                    results
                  </p>
                  {candidates.map((candidate) => (
                    <CandidateCardForRecruiter
                      key={candidate.id}
                      candidate={candidate}
                    />
                  ))}

                  <LoadMoreButton
                    loading={loading}
                    hasNext={pagination.hasNext}
                    page={page}
                    size={pagination.size}
                    totalElements={pagination.totalElements}
                    currentCount={candidates.length}
                    setPage={setPage}
                    variant="dark"
                  />
                </>
              )}
            </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}

// --- Helper Components ---

function formatDisplayValue(config: FilterItem, value: any) {
  if (config.filterType === "BOOLEAN") return value ? "Yes" : "No";
  if (config.filterType === "MULTI") return value.join(', ');
  if (config.filterType === "RANGE") return `${value.min || 0} - ${value.max || '∞'}`;
  return value;
}

interface FilterSectionProps {
  config: FilterItem;
  value: any;
  onChange: (val: any) => void;
  isOpen: boolean;
  onToggle: () => void;
}

function FilterSection({ config, value, onChange, isOpen, onToggle }: FilterSectionProps) {
  const renderInput = () => {
    switch (config.filterType) {
      case "MULTI":
        return (
          <div className="space-y-2 mt-4">
            {MOCK_OPTIONS.map((opt) => {
              const isChecked = value?.includes(opt);
              return (
                <label key={opt} className="flex items-center gap-3 group cursor-pointer p-2 hover:bg-slate-50 rounded-xl transition-all">
                  <div className={`w-5 h-5 rounded-lg border-2 transition-all flex items-center justify-center ${isChecked ? 'bg-blue-600 border-blue-600' : 'border-slate-200 bg-white group-hover:border-blue-400'}`}>
                    {isChecked && <CheckIcon className="w-3.5 h-3.5 text-white" />}
                  </div>
                  <input 
                    type="checkbox" 
                    className="hidden" 
                    checked={isChecked} 
                    onChange={() => {
                      const newValue = isChecked ? value.filter((v: any) => v !== opt) : [...(value || []), opt];
                      onChange(newValue);
                    }} 
                  />
                  <span className={`text-[13px] font-medium ${isChecked ? 'text-slate-900' : 'text-slate-500'}`}>{opt}</span>
                </label>
              );
            })}
          </div>
        );
      case "RANGE":
        return (
          <div className="flex items-center gap-3 mt-4">
            <input type="number" placeholder="Min" className="w-full px-3 py-2.5 text-xs font-bold bg-slate-50 border border-slate-200 rounded-xl outline-none focus:bg-white focus:border-blue-500 transition-all" value={value?.min || ''} onChange={(e) => onChange({ ...value, min: e.target.value })} />
            <span className="text-slate-300">-</span>
            <input type="number" placeholder="Max" className="w-full px-3 py-2.5 text-xs font-bold bg-slate-50 border border-slate-200 rounded-xl outline-none focus:bg-white focus:border-blue-500 transition-all" value={value?.max || ''} onChange={(e) => onChange({ ...value, max: e.target.value })} />
          </div>
        );
      case "BOOLEAN":
        return (
          <div className="flex p-1 bg-slate-100 rounded-xl mt-4">
            {[true, false].map((bool) => (
              <button key={String(bool)} onClick={() => onChange(bool)} className={`flex-1 py-2 text-[10px] font-black rounded-lg transition-all ${value === bool ? "bg-white text-blue-600 shadow-sm" : "text-slate-400 hover:text-slate-600"}`}>
                {bool ? "ACTIVE" : "ANY"}
              </button>
            ))}
          </div>
        );
      case "GT":
      case "GTE":
      case "LT":
      case "LTE":
        const labels: Record<string, string> = { "GT": "Greater Than", "GTE": "Greater Than & Equal", "LT": "Less Than", "LTE": "Less Than & Equal" };
        return (
          <div className="mt-4 space-y-2">
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-tighter">{labels[config.filterType]}</p>
            <input type="number" placeholder="Enter value..." className="w-full px-4 py-3 text-xs font-bold bg-slate-50 border border-slate-200 rounded-xl outline-none focus:bg-white focus:border-blue-500 transition-all" value={value || ''} onChange={(e) => onChange(e.target.value)} />
          </div>
        );
      default:
        return (
          <div className="relative mt-4">
            <MagnifyingGlassIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input type="text" placeholder={`Search ${config.label}...`} className="w-full pl-11 pr-4 py-3 text-xs font-bold bg-slate-50 border border-slate-200 rounded-xl outline-none focus:bg-white focus:border-blue-500 transition-all" value={value || ''} onChange={(e) => onChange(e.target.value)} />
          </div>
        );
    }
  };

  return (
    <div className="px-6 py-5 hover:bg-slate-50/50 transition-colors">
      <div 
        className="flex items-center justify-between cursor-pointer group"
        onClick={onToggle}
      >
        <h4 className="text-[11px] font-black text-slate-900/40 uppercase tracking-[0.15em] group-hover:text-blue-600 transition-colors">
          {config.label}
        </h4>
        <div className={`p-1 rounded-md transition-all ${isOpen ? 'bg-blue-50 text-blue-600' : 'text-slate-300'}`}>
          <ChevronDownIcon className={`w-4 h-4 transition-transform duration-500 ${isOpen ? 'rotate-180' : ''}`} />
        </div>
      </div>

      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ type: 'spring', duration: 0.4, bounce: 0 }}
            className="overflow-hidden"
          >
            {renderInput()}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}